import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Search, User, LogOut, Settings, LayoutGrid, List, Menu, LayoutDashboard, Bookmark, CreditCard, SlidersHorizontal, Rss, Receipt, Shield, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { isAuthenticated, logout, getViewMode, setViewMode as saveViewMode } from '@/lib/utils'
import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { dashboardApi } from '@/services/api'

export function Navbar({ onViewModeChange, onFilterToggle, showFilterButton = false }) {
  const location = useLocation()
  const [viewMode, setViewModeState] = useState(getViewMode())
  const [menuOpen, setMenuOpen] = useState(false)
  const authenticated = isAuthenticated()
  const navigate = useNavigate()
  const menuRef = useRef(null)

  // Fetch user profile for profile picture
  const { data: userProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await dashboardApi.getProfile()
      return response.data
    },
    enabled: authenticated, // Only fetch if user is authenticated
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (userProfile?.first_name) {
      return userProfile.first_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    if (userProfile?.email) {
      return userProfile.email[0].toUpperCase()
    }
    return 'U'
  }

  const handleViewModeToggle = () => {
    const newMode = viewMode === 'card' ? 'list' : 'card'
    setViewModeState(newMode)
    saveViewMode(newMode)
    onViewModeChange?.(newMode)
  }

  const handleLogout = () => {
    logout()
    toast.success('Logged out successfully')
    setMenuOpen(false)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  return (
    <nav className="sticky-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2">
              <div data-logo-box className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <span data-logo-text className="text-xl font-bold bg-clip-text text-transparent transition-all duration-300">
                Newsne.ws
              </span>
            </Link>

            {/* Desktop Navigation */}
            {/* {!authenticated && (
              <div className="hidden md:flex items-center gap-4">
                <Link to="/pricing" className="text-sm font-medium text-gray-700 hover:text-primary transition-colors">
                  Pricing
                </Link>
              </div>
            )} */}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* View mode toggle - single button that toggles on/off */}
            {onViewModeChange && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-gray-700 dark:text-gray-300"
                onClick={handleViewModeToggle}
                title={viewMode === 'card' ? 'Switch to list view' : 'Switch to grid view'}
              >
                {viewMode === 'card' ? <List className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
              </Button>
            )}

            {/* Main navigation menu (hamburger) */}
            {authenticated ? (
              <div className="relative" ref={menuRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="h-9 w-9 text-gray-700 dark:text-gray-300"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>

                {/* Dropdown Menu */}
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <Link
                      to="/dashboard"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname === '/dashboard'
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Overview
                    </Link>
                    <Link
                      to="/dashboard/feed"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/feed')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Rss className="h-4 w-4" />
                      My Feed
                    </Link>
                    <Link
                      to="/dashboard/curation"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/curation')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Users className="h-4 w-4" />
                      Curation
                    </Link>
                    <Link
                      to="/dashboard/bookmarks"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/bookmarks')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Bookmark className="h-4 w-4" />
                      Bookmarks
                    </Link>
                    <Link
                      to="/dashboard/subscription"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/subscription')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <CreditCard className="h-4 w-4" />
                      Subscription
                    </Link>
                    <Link
                      to="/dashboard/transactions"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/transactions')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Receipt className="h-4 w-4" />
                      Transactions
                    </Link>
                    <Link
                      to="/dashboard/settings"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/settings')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </Link>
                    <Link
                      to="/dashboard/security"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${location.pathname.startsWith('/dashboard/security')
                        ? 'bg-slate-50 dark:bg-slate-800 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Shield className="h-4 w-4" />
                      Security
                    </Link>

                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-700 dark:text-gray-300">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* User avatar - links to account, no dropdown */}
            {authenticated && (
              <Link
                to="/dashboard/account"
                className="relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
              >
                <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-primary transition-all">
                  <AvatarImage
                    src={userProfile?.profile_picture || userProfile?.avatar}
                    alt={userProfile?.first_name || 'User'}
                  />
                  <AvatarFallback className="bg-primary text-white font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Link>
            )}

            {/* Mobile filter button - only on home page */}
            {showFilterButton && (
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={onFilterToggle}
              >
                <SlidersHorizontal className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
